import { Trash2, ArrowLeft, ArrowRight, Mail, UserX, Clock, Database, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../config/company';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { LEGAL_CONTENT, fillPlaceholders } from '../config/legalContent';

// خريطة الأيقونات — تسمح للملف المركزي بإسناد أيقونة بواسطة اسمها فقط
const ICONS = {
  Mail: <Mail className="h-5 w-5" />,
  ShieldCheck: <ShieldCheck className="h-5 w-5" />,
  Database: <Database className="h-5 w-5" />,
  Clock: <Clock className="h-5 w-5" />,
};

export default function DataDeletion() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const lang = isAr ? 'ar' : 'en';
  const BackArrow = isAr ? ArrowRight : ArrowLeft;

  const D = LEGAL_CONTENT.dataDeletion;
  const placeholders = { email: COMPANY_INFO.email, phone: COMPANY_INFO.phoneDisplay };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'} data-testid="data-deletion-page">
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={`absolute top-20 start-1/3 w-[400px] h-[400px] ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/5'} rounded-full blur-[100px]`} />
      </div>

      {/* Compact Header */}
      <section className="relative pt-10 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#1E9BD9] hover:underline mb-6 text-sm" data-testid="data-deletion-back-home">
            <BackArrow className="h-4 w-4" />
            {t('pages.backToHome')}
          </Link>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-500/10'}`}>
              <UserX className="h-7 w-7 text-red-500" />
            </div>
            <div>
              <h1 className={`text-3xl sm:text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} data-testid="data-deletion-title">
                {D.title[lang]}
              </h1>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                {D.subtitle[lang]}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`rounded-2xl p-6 md:p-10 ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border border-[#E2E8F0] shadow-sm'}`}>
            <p className={`mb-8 leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>
              {D.intro[lang]}
            </p>

            <div className="space-y-8">
              {D.steps.map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 flex flex-col items-center">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/10'}`}>
                      <div className="text-[#1E9BD9]">{ICONS[step.iconKey]}</div>
                    </div>
                    {index < D.steps.length - 1 && (
                      <div className={`w-px flex-1 mt-2 ${isDarkMode ? 'bg-white/10' : 'bg-[#E2E8F0]'}`} />
                    )}
                  </div>
                  <div className="pb-2">
                    <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      <span className="text-[#1E9BD9] me-2">{index + 1}.</span>{step[lang].title}
                    </h3>
                    <p className={`leading-relaxed text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                      {fillPlaceholders(step[lang].content, placeholders)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Request button */}
            <a
              href={`mailto:${COMPANY_INFO.email}?subject=${encodeURIComponent(D.emailSubject[lang])}&body=${encodeURIComponent(D.emailBody[lang])}`}
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium transition-colors"
              data-testid="data-deletion-request-btn"
            >
              <Trash2 className="h-4 w-4" />
              {D.ctaButton[lang]}
            </a>

            {/* Retained data note */}
            <div className={`mt-8 p-4 rounded-xl ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-amber-200/90' : 'text-[#92400E]'}`}>
                {D.retainedNote[lang]}
              </p>
            </div>

            <div className={`mt-10 pt-8 border-t ${isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]'}`}>
              <p className={isDarkMode ? 'text-gray-400' : 'text-[#475569]'}>
                {D.contact[lang]}
                <a href={`mailto:${COMPANY_INFO.email}`} className="text-[#1E9BD9] hover:underline">
                  {COMPANY_INFO.email}
                </a>
                {' • '}
                <span dir="ltr">{COMPANY_INFO.phoneDisplay}</span>
              </p>
              <p className={`text-sm mt-4 ${isDarkMode ? 'text-gray-600' : 'text-[#94A3B8]'}`}>
                {LEGAL_CONTENT.lastUpdated[lang]}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
